/**
 * OpsTeamAnalyticsPage — read-only dashboard giving Operations Managers
 * and the Executive Director a real-time view of team workload,
 * completion rates, and project pipeline.
 * Route: /ops/analytics/team (executive_director + operations_manager)
 */
import { useEffect, useMemo, useState } from 'react';
import OpsLayout from './OpsLayout';
import { opsApi } from './OpsAuthContext';

const StatCard = ({ label, value, hint, accent = '#1F3D2B' }) => (
  <div
    className="bg-white rounded-2xl border border-[#E6E0D6] p-5"
    data-testid={`kpi-${label}`}
  >
    <div className="text-[11px] tracking-[0.14em] uppercase text-[#8B937F] font-medium mb-2">
      {label}
    </div>
    <div className="text-3xl font-['Playfair_Display'] font-bold" style={{ color: accent }}>
      {value}
    </div>
    {hint && <div className="text-[11px] text-[#8B937F] mt-1">{hint}</div>}
  </div>
);

const Bar = ({ value, max, color = '#4A641F' }) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-1.5 rounded-full bg-[#F0EBE2] overflow-hidden">
      <div
        className="h-full rounded-full transition-all"
        style={{ width: `${pct}%`, background: color }}
      />
    </div>
  );
};

const Badge = ({ children, tone = 'neutral' }) => {
  const map = {
    neutral: { bg: '#F5F2EC', color: '#3D512D' },
    warm: { bg: '#FEF4E5', color: '#8B6F3E' },
    green: { bg: '#EEF5EF', color: '#3D512D' },
    gold: { bg: '#F5E6C4', color: '#8B6F3E' },
  };
  const s = map[tone] || map.neutral;
  return (
    <span
      className="inline-block px-2.5 py-0.5 rounded-full text-[10.5px] font-semibold tracking-wide"
      style={{ background: s.bg, color: s.color }}
    >
      {children}
    </span>
  );
};

const OpsTeamAnalyticsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const { data: d } = await opsApi.get('/analytics/team');
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr('تعذّر تحميل مؤشرات الفريق');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const totals = data?.totals || {};
  const members = data?.members || [];

  const maxOpenTasks = useMemo(
    () => Math.max(1, ...members.map((m) => m.tasks_open)),
    [members]
  );
  const maxLeadingActive = useMemo(
    () => Math.max(1, ...members.map((m) => m.leading_active)),
    [members]
  );

  const filtered = useMemo(() => {
    return members.filter((m) => {
      if (roleFilter !== 'all' && m.role !== roleFilter) return false;
      if (q.trim()) {
        const s = q.trim().toLowerCase();
        if (!m.name_ar.toLowerCase().includes(s) && !m.email.toLowerCase().includes(s))
          return false;
      }
      return true;
    });
  }, [members, q, roleFilter]);

  const generatedAt = data?.generated_at
    ? new Date(data.generated_at).toLocaleString('ar-SA', {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '';

  return (
    <OpsLayout
      title="مؤشرات الفريق"
      subtitle="رؤية فورية لحمل العمل، معدل الإنجاز، وتقدم المسارات — دون اجتماعات دورية."
    >
      {loading && (
        <div className="text-center py-16 text-[#7A866E]" data-testid="loading">
          جارٍ التحميل…
        </div>
      )}
      {err && (
        <div
          className="bg-[#FEF4E5] border border-[#F0C97A] text-[#8B6F3E] p-4 rounded-xl text-sm"
          data-testid="error"
        >
          {err}
        </div>
      )}

      {data && !loading && (
        <>
          {/* Header KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <StatCard label="حجم الفريق" value={totals.team_size} />
            <StatCard
              label="مشاريع نشطة"
              value={totals.active_projects}
              accent="#4A641F"
            />
            <StatCard
              label="مشاريع مُسلَّمة"
              value={totals.delivered_projects}
              accent="#B08A50"
            />
            <StatCard label="مشاريع مُغلقة" value={totals.closed_projects} />
            <StatCard
              label="مهام مفتوحة"
              value={totals.open_tasks}
              accent="#8B6F3E"
            />
            <StatCard
              label="مهام مُنجزة"
              value={totals.done_tasks}
              accent="#3D512D"
            />
          </div>

          {/* Filter bar */}
          <div className="bg-white rounded-2xl border border-[#E6E0D6] p-4 mb-4 flex flex-wrap items-center gap-3">
            <input
              data-testid="filter-search"
              type="search"
              placeholder="ابحث بالاسم أو البريد…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="flex-1 min-w-[220px] px-4 py-2 text-sm rounded-lg border border-[#E6E0D6] focus:outline-none focus:border-[#4A641F]"
            />
            <div className="flex gap-1.5 flex-wrap">
              {[
                { id: 'all', label: 'الكل' },
                { id: 'operations_manager', label: 'مسؤول التشغيل' },
                { id: 'track_leader', label: 'قائد مسار' },
                { id: 'member', label: 'عضو فريق' },
              ].map((r) => (
                <button
                  key={r.id}
                  type="button"
                  data-testid={`role-${r.id}`}
                  onClick={() => setRoleFilter(r.id)}
                  className={
                    'px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ' +
                    (roleFilter === r.id
                      ? 'bg-[#1F3D2B] text-white border-[#1F3D2B]'
                      : 'bg-white text-[#3D512D] border-[#E6E0D6] hover:border-[#4A641F]')
                  }
                >
                  {r.label}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-[#8B937F]">
              {filtered.length} من {members.length} عضواً
            </div>
          </div>

          {/* Members grid */}
          <div
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="analytics-grid"
          >
            {filtered.map((m) => (
              <div
                key={m.email}
                data-testid={`member-${m.email}`}
                className="bg-white rounded-2xl border border-[#E6E0D6] p-5 hover:border-[#4A641F] transition-colors flex flex-col"
              >
                <div className="flex items-start justify-between gap-3 pb-4 border-b border-[#F0EBE2]">
                  <div className="min-w-0">
                    <div className="font-bold text-[15px] text-[#1F3D2B] truncate">
                      {m.name_ar}
                    </div>
                    <div className="text-[11px] text-[#8B937F] truncate">
                      {m.email}
                    </div>
                    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                      <Badge tone={m.role === 'operations_manager' ? 'gold' : 'green'}>
                        {m.role_label_ar}
                      </Badge>
                      {!m.active && <Badge tone="warm">غير نشط</Badge>}
                    </div>
                  </div>
                  <div className="text-left flex-shrink-0">
                    <div className="text-[10.5px] tracking-wide text-[#8B937F] mb-0.5">
                      إنجاز
                    </div>
                    <div className="text-2xl font-['Playfair_Display'] font-bold text-[#4A641F]">
                      {m.completion_rate}%
                    </div>
                  </div>
                </div>

                {/* Tasks metrics */}
                <div className="grid grid-cols-2 gap-3 pt-4 pb-3">
                  <div>
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-[11px] text-[#8B937F]">مهام مفتوحة</span>
                      <span className="text-[13px] font-bold text-[#8B6F3E]">
                        {m.tasks_open}
                      </span>
                    </div>
                    <Bar value={m.tasks_open} max={maxOpenTasks} color="#B08A50" />
                  </div>
                  <div>
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-[11px] text-[#8B937F]">مهام مُنجزة</span>
                      <span className="text-[13px] font-bold text-[#3D512D]">
                        {m.tasks_done}
                      </span>
                    </div>
                    <Bar value={m.tasks_done} max={Math.max(1, m.tasks_open + m.tasks_done)} />
                  </div>
                </div>

                {/* Projects metrics */}
                <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[#F0EBE2]">
                  <div className="text-center">
                    <div className="text-[10px] text-[#8B937F] mb-1">مشاريع تقود</div>
                    <div className="text-[15px] font-bold text-[#1F3D2B]">
                      {m.leading_active}
                    </div>
                    {m.leading_active > 0 && (
                      <div className="mt-1">
                        <Bar
                          value={m.leading_active}
                          max={maxLeadingActive}
                          color="#4A641F"
                        />
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] text-[#8B937F] mb-1">مشاريع تشارك</div>
                    <div className="text-[15px] font-bold text-[#3D512D]">
                      {m.member_active}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] text-[#8B937F] mb-1">مُسلَّمة</div>
                    <div className="text-[15px] font-bold text-[#B08A50]">
                      {m.leading_delivered}
                    </div>
                  </div>
                </div>

                {/* Bottom row: avg progress + on-time */}
                <div className="mt-4 pt-3 border-t border-[#F0EBE2] flex items-center justify-between text-[11px]">
                  <div>
                    <span className="text-[#8B937F]">متوسط تقدم المسارات:</span>{' '}
                    <span className="font-bold text-[#1F3D2B]">
                      {m.avg_project_progress}%
                    </span>
                  </div>
                  <div>
                    <span className="text-[#8B937F]">التزام المواعيد:</span>{' '}
                    <span className="font-bold text-[#4A641F]">
                      {m.ontime_sample > 0 ? `${m.ontime_rate}%` : '—'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div
              data-testid="empty-state"
              className="text-center py-12 text-[#8B937F]"
            >
              لا توجد نتائج مطابقة.
            </div>
          )}

          {generatedAt && (
            <div className="mt-6 text-[11px] text-[#8B937F] text-center italic">
              آخر تحديث: {generatedAt}
            </div>
          )}
        </>
      )}
    </OpsLayout>
  );
};

export default OpsTeamAnalyticsPage;
